import { FormEvent, ReactNode, useMemo, useState } from "react";
import blandSchedule from "./data/bland26/bland.json";
import stageData from "./data/bland26/stages.json";
import iowaSchedule from "./data/iowa26/iowa.json";
import iowaStageData from "./data/iowa26/iowa-stages.json";
import laurensSchedule from "./data/laurens26/laurens.json";
import laurensStageData from "./data/laurens26/laurens-stages.json";
import montanaSchedule from "./data/montana26/montana.json";
import montanaStageData from "./data/montana26/montana-stages.json";
import ncSchedule from "./data/nc26/nc.json";
import ncStageData from "./data/nc26/nc-stages.json";
import ohioSchedule from "./data/ohio26/ohio.json";
import ohioStageData from "./data/ohio26/ohio-stages.json";
import phoenixSchedule from "./data/phoenix26/phoenix.json";
import phoenixStageData from "./data/phoenix26/phoenix-stages.json";
import rupertSchedule from "./data/rupert26/rupert.json";
import rupertStageData from "./data/rupert26/rupert-stages.json";
import vegasSkirmishSeptSchedule from "./data/vegasskirmishsept26/vegas.json";
import vegasSkirmishSeptStageData from "./data/vegasskirmishsept26/vegas-stages.json";

type Day = "Saturday" | "Sunday";

type ScheduleEntry = {
  stage: string;
  stageBriefTime?: string;
  stageTime: string;
};

type LoadoutItem = {
  magazines: number;
  rounds: number;
};

type FirearmLoadoutValue = LoadoutItem | string | null;

type Loadout = {
  rifle: FirearmLoadoutValue;
  pistol: FirearmLoadoutValue;
  notes: string | null;
};

type TeamInfo = {
  loadout: Loadout | null;
  firingSequence: string | null;
  stageDescription: string | null;
};

type StageInfo = {
  stageNumber: number;
  title: string;
  points: number;
  bayLocation: string | null;
  type: string | null;
  cap: string | null;
  ro: string | null;
  loadout: Loadout;
  gear: string | null;
  firingSequence: string | null;
  targets: string | null;
  stageDescription: string | null;
  movementStandards: string[];
  penalties: string[];
  teams: TeamInfo | null;
  caloriesAndWeights: string[];
  weights: string[];
  reps: string[];
};

type Schedule = Record<string, Partial<Record<Day, ScheduleEntry[]>>>;
type Stages = Record<string, StageInfo>;

const days: Day[] = ["Saturday", "Sunday"];

type Selection = {
  matrixNumber: string;
  day: Day;
};

type EventConfig = {
  label: string;
  path: string;
  schedule: Schedule;
  stages: Stages;
  days?: Day[];
  category?: "event" | "skirmish";
  hiddenFromChooser?: boolean;
};

const events: EventConfig[] = [
  {
    label: "Phoenix 2026",
    path: "/phoenix2026",
    schedule: phoenixSchedule as Schedule,
    stages: phoenixStageData as Stages
  },
  {
    label: "North Carolina 2026",
    path: "/nc2026",
    schedule: ncSchedule as Schedule,
    stages: ncStageData as Stages
  },
  {
    label: "Vegas Skirmish Sept 2026",
    path: "/vegasskirmishsept2026",
    schedule: vegasSkirmishSeptSchedule as Schedule,
    stages: vegasSkirmishSeptStageData as Stages,
    days: ["Sunday"],
    category: "skirmish"
  },
  {
    label: "Ohio 2026",
    path: "/ohio2026",
    schedule: ohioSchedule as Schedule,
    stages: ohioStageData as Stages
  },
  {
    label: "Montana 2026",
    path: "/montana2026",
    schedule: montanaSchedule as Schedule,
    stages: montanaStageData as Stages
  },
  {
    label: "Iowa 2026",
    path: "/iowa2026",
    schedule: iowaSchedule as Schedule,
    stages: iowaStageData as Stages
  },
  {
    label: "Rupert 2026",
    path: "/rupert2026",
    schedule: rupertSchedule as Schedule,
    stages: rupertStageData as Stages
  },
  {
    label: "Laurens 2026",
    path: "/laurens2026",
    schedule: laurensSchedule as Schedule,
    stages: laurensStageData as Stages
  },
  {
    label: "Bland 2026",
    path: "/bland2026",
    schedule: blandSchedule as Schedule,
    stages: stageData as Stages
  }
];

export function App() {
  const currentPath = normalizePath(window.location.pathname);
  const event = events.find((eventOption) => eventOption.path === currentPath);

  if (currentPath === "/") {
    return <EventChooser />;
  }

  if (!event) {
    return <NotFound />;
  }

  return <EventPlanner event={event} key={event.path} />;
}

function EventChooser() {
  const visibleEvents = events.filter((event) => !event.hiddenFromChooser);
  const mainEvents = visibleEvents.filter((event) => event.category !== "skirmish");
  const skirmishEvents = visibleEvents.filter(
    (event) => event.category === "skirmish"
  );

  return (
    <main className="app-shell">
      <section className="workspace">
        <header className="app-header">
          <div>
            <p className="eyebrow">TacPlanner</p>
            <h1>Event Planner</h1>
          </div>
        </header>

        <EventSection
          events={mainEvents}
          title="Two-Day Events"
          ariaLabel="Two-day event planners"
        />
        <EventSection
          events={skirmishEvents}
          title="One-Day Skirmish"
          ariaLabel="One-day skirmish planners"
        />
      </section>
    </main>
  );
}

function NotFound() {
  const visibleEvents = events.filter((event) => !event.hiddenFromChooser);
  const mainEvents = visibleEvents.filter((event) => event.category !== "skirmish");
  const skirmishEvents = visibleEvents.filter(
    (event) => event.category === "skirmish"
  );

  return (
    <main className="app-shell">
      <section className="workspace">
        <header className="app-header">
          <div>
            <p className="eyebrow">Not Found</p>
            <h1>Choose an Event</h1>
          </div>
        </header>

        <p className="muted">That event planner path is not available.</p>
        <EventSection
          events={mainEvents}
          title="Two-Day Events"
          ariaLabel="Two-day event planners"
        />
        <EventSection
          events={skirmishEvents}
          title="One-Day Skirmish"
          ariaLabel="One-day skirmish planners"
        />
      </section>
    </main>
  );
}

function EventSection({
  ariaLabel,
  events,
  title
}: {
  ariaLabel: string;
  events: EventConfig[];
  title: string;
}) {
  if (events.length === 0) {
    return null;
  }

  return (
    <section className="event-section">
      <h2>{title}</h2>
      <nav className="event-grid" aria-label={ariaLabel}>
        {events.map((event) => (
          <a className="event-card" href={event.path} key={event.path}>
            <span className="eyebrow">Open planner</span>
            <strong>{event.label}</strong>
            <span>{eventCardMeta(event)}</span>
          </a>
        ))}
      </nav>
    </section>
  );
}

function EventPlanner({ event }: { event: EventConfig }) {
  const eventDays = getEventDays(event);
  const singleDay = eventDays.length === 1 ? eventDays[0] : null;
  const savedSelection = readSavedSelection(event);
  const [matrixNumber, setMatrixNumber] = useState(
    savedSelection?.matrixNumber ?? ""
  );
  const [day, setDay] = useState<Day | "">(
    savedSelection?.day ?? singleDay ?? ""
  );
  const [pickerExpanded, setPickerExpanded] = useState(!savedSelection);
  const [submittedSelection, setSubmittedSelection] =
    useState<Selection | null>(savedSelection);

  const matrixNumbers = useMemo(
    () =>
      Object.keys(event.schedule).sort(
        (first, second) => Number(first) - Number(second)
      ),
    [event.schedule]
  );

  const selectedEntries = useMemo(() => {
    if (!submittedSelection) {
      return [];
    }

    return (
      event.schedule[submittedSelection.matrixNumber]?.[
        submittedSelection.day
      ] ?? []
    );
  }, [event.schedule, submittedSelection]);

  function handleSubmit(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();

    const selectedDay = singleDay ?? day;

    if (!matrixNumber || !selectedDay) {
      return;
    }

    const selection = { matrixNumber, day: selectedDay };
    saveSelection(event, selection);
    setSubmittedSelection(selection);
    setPickerExpanded(false);
  }

  function handleClearSelection() {
    clearSavedSelection(event);
    setMatrixNumber("");
    setDay(singleDay ?? "");
    setSubmittedSelection(null);
    setPickerExpanded(true);
  }

  return (
    <main className="app-shell">
      <section className="workspace">
        <header className="app-header">
          <div>
            <p className="eyebrow">{event.label}</p>
          </div>
        </header>

        <section className="picker-panel">
          {submittedSelection ? (
            <div className="picker-actions">
              <button
                aria-expanded={pickerExpanded}
                className="picker-toggle"
                type="button"
                onClick={() => setPickerExpanded((current) => !current)}
              >
                <span>
                  Squad {submittedSelection.matrixNumber} |{" "}
                  {submittedSelection.day}
                </span>
                <strong>{pickerExpanded ? "Hide picker" : "Change"}</strong>
              </button>
              <button
                className="clear-selection"
                type="button"
                onClick={handleClearSelection}
              >
                Clear
              </button>
            </div>
          ) : null}

          {pickerExpanded ? (
            <form className="lookup-form" onSubmit={handleSubmit}>
              <label>
                Squads
                <select
                  value={matrixNumber}
                  onChange={(event) => setMatrixNumber(event.target.value)}
                >
                  <option value="">Select squad</option>
                  {matrixNumbers.map((number) => (
                    <option key={number} value={number}>
                      {number}
                    </option>
                  ))}
                </select>
              </label>

              {singleDay ? (
                <div className="single-day-field">
                  <span>Day</span>
                  <strong>{singleDay}</strong>
                </div>
              ) : (
                <label>
                  Day
                  <select
                    value={day}
                    onChange={(event) => setDay(event.target.value as Day | "")}
                  >
                    <option value="">Select day</option>
                    {eventDays.map((dayOption) => (
                      <option key={dayOption} value={dayOption}>
                        {dayOption}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <button type="submit" disabled={!matrixNumber || !(singleDay ?? day)}>
                Show stages
              </button>
            </form>
          ) : null}
        </section>

        {submittedSelection ? (
          <section className="schedule-view" aria-live="polite">
            <div className="schedule-heading">
              <div>
                <p className="eyebrow">Squad {submittedSelection.matrixNumber}</p>
                <h2>{submittedSelection.day}</h2>
              </div>
              <button
                className="download-plan"
                type="button"
                onClick={() =>
                  downloadStagePlanPdf(event, submittedSelection, selectedEntries)
                }
              >
                Download PDF
              </button>
            </div>

            <div className="stage-list">
              {selectedEntries.map((entry, index) => (
                <ScheduleItem
                  entry={entry}
                  key={`${entry.stage}-${entry.stageTime}-${index}`}
                  stages={event.stages}
                />
              ))}
            </div>
          </section>
        ) : (
          <p className="muted">
            {singleDay
              ? "Select a squad to view the ordered stage plan."
              : "Select a matrix number and day to view the ordered stage plan."}
          </p>
        )}
      </section>
    </main>
  );
}

function ScheduleItem({
  entry,
  stages
}: {
  entry: ScheduleEntry;
  stages: Stages;
}) {
  if (entry.stage === "Safety Brief") {
    return (
      <article className="safety-row">
        <time>{formatStageTime(entry.stageTime)}</time>
        <div className="safety-copy">
          <h3>Safety Brief</h3>
          <p>Mandatory for all competitors</p>
        </div>
      </article>
    );
  }

  const stageNumber = readStageNumber(entry.stage);
  const stage = stageNumber ? stages[String(stageNumber)] : undefined;

  if (!stage) {
    return (
      <article className="fallback-row">
        <time>{formatStageTime(entry.stageTime)}</time>
        <div>
          <h3>{entry.stage}</h3>
          <p>Stage details are not available.</p>
        </div>
      </article>
    );
  }

  return <StageCard entry={entry} stage={stage} />;
}

function StageCard({
  entry,
  stage
}: {
  entry: ScheduleEntry;
  stage: StageInfo;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <article className={`stage-card ${expanded ? "expanded" : ""}`}>
      <button
        aria-expanded={expanded}
        className="stage-summary"
        type="button"
        onClick={() => setExpanded((current) => !current)}
      >
        <StageTimes entry={entry} />
        <div className="stage-summary-copy">
          <p className="stage-kicker">{entry.stage}</p>
          <h3>{stage.title}</h3>
          <SummaryLoadout loadout={stage.loadout} />
        </div>
        <span className="expand-indicator">{expanded ? "Hide" : "Details"}</span>
      </button>

      {expanded ? (
        <div className="stage-details">
          <dl className="meta-grid">
            <MetaItem label="Bay" value={stage.bayLocation} />
            <MetaItem label="Type" value={stage.type} />
            <MetaItem label="Cap" value={stage.cap} />
            <MetaItem label="RO" value={stage.ro} />
          </dl>

          <Section title="Loadout">
            <LoadoutView loadout={stage.loadout} />
          </Section>

          <dl className="detail-list">
            <MetaItem label="Gear" value={stage.gear} />
            <MetaItem label="Firing Sequence" value={stage.firingSequence} />
            <MetaItem label="Targets" value={stage.targets} />
          </dl>

          {stage.stageDescription ? (
            <Section title="Stage Description">
              <p>{stage.stageDescription}</p>
            </Section>
          ) : null}

          <StringList
            title="Movement Standards"
            values={stage.movementStandards}
          />
          <StringList title="Penalties" values={stage.penalties} />
          <StringList title="Reps" values={stage.reps} />
          <StringList title="Weights" values={stage.weights} />
          <StringList
            title="Calories and Weights"
            values={stage.caloriesAndWeights}
          />

          {stage.teams ? <TeamSection teams={stage.teams} /> : null}
        </div>
      ) : null}
    </article>
  );
}

function TeamSection({ teams }: { teams: TeamInfo }) {
  if (!teams.loadout && !teams.firingSequence && !teams.stageDescription) {
    return null;
  }

  return (
    <Section title="Teams">
      {teams.loadout ? <TeamLoadoutView loadout={teams.loadout} /> : null}
      <dl className="detail-list">
        <MetaItem label="Firing Sequence" value={teams.firingSequence} />
      </dl>
      {teams.stageDescription ? <p>{teams.stageDescription}</p> : null}
    </Section>
  );
}

function TeamLoadoutView({ loadout }: { loadout: Loadout }) {
  if (
    loadout.notes &&
    loadout.rifle === "None" &&
    loadout.pistol === "None"
  ) {
    return (
      <div className="loadout-grid">
        <div className="loadout-item">
          <span>Loadout</span>
          <strong>{loadout.notes}</strong>
        </div>
      </div>
    );
  }

  return <LoadoutView loadout={loadout} />;
}

function SummaryLoadout({ loadout }: { loadout: Loadout }) {
  return (
    <div className="summary-loadout">
      <SummaryLoadoutItem label="Rifle" loadout={loadout.rifle} />
      <SummaryLoadoutItem label="Pistol" loadout={loadout.pistol} />
    </div>
  );
}

function SummaryLoadoutItem({
  label,
  loadout
}: {
  label: string;
  loadout: FirearmLoadoutValue;
}) {
  return (
    <span>
      <strong>{label}</strong>
      {formatLoadoutValue(loadout)}
    </span>
  );
}

function StageTimes({ entry }: { entry: ScheduleEntry }) {
  return (
    <div className="time-stack">
      {entry.stageBriefTime ? (
        <div>
          <span>Brief</span>
          <time>{formatStageTime(entry.stageBriefTime)}</time>
        </div>
      ) : null}
      <div>
        {entry.stageBriefTime ? <span>Stage</span> : null}
        <time>{formatStageTime(entry.stageTime)}</time>
      </div>
    </div>
  );
}

function LoadoutView({ loadout }: { loadout: Loadout | null }) {
  if (!loadout) {
    return <p className="muted compact">No loadout listed.</p>;
  }

  return (
    <div className="loadout-grid">
      <FirearmLoadout label="Rifle" loadout={loadout.rifle} />
      <FirearmLoadout label="Pistol" loadout={loadout.pistol} />
      {loadout.notes ? <p className="loadout-note">{loadout.notes}</p> : null}
    </div>
  );
}

function FirearmLoadout({
  label,
  loadout
}: {
  label: string;
  loadout: FirearmLoadoutValue;
}) {
  return (
    <div className="loadout-item">
      <span>{label}</span>
      {typeof loadout === "string" ? (
        <strong>{loadout}</strong>
      ) : loadout ? (
        <strong>{formatLoadoutValue(loadout)}</strong>
      ) : (
        <strong>As listed</strong>
      )}
    </div>
  );
}

function formatLoadoutValue(loadout: FirearmLoadoutValue) {
  if (typeof loadout === "string") {
    return loadout;
  }

  if (loadout) {
    return `${loadout.magazines} mags | ${loadout.rounds} rounds`;
  }

  return "As listed";
}

function StringList({
  title,
  values
}: {
  title: string;
  values: string[];
}) {
  if (values.length === 0) {
    return null;
  }

  return (
    <Section title={title}>
      <ul>
        {values.map((value) => (
          <li key={value}>{value}</li>
        ))}
      </ul>
    </Section>
  );
}

function Section({
  children,
  title
}: {
  children: ReactNode;
  title: string;
}) {
  return (
    <section className="stage-section">
      <h4>{title}</h4>
      {children}
    </section>
  );
}

function MetaItem({
  label,
  value
}: {
  label: string;
  value: string | number | null;
}) {
  if (value === null || value === "") {
    return null;
  }

  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function readStageNumber(stage: string) {
  const match = /^Stage\s+(\d+)$/i.exec(stage);
  return match ? Number(match[1]) : null;
}

function formatStageTime(stageTime: string) {
  if (!/^\d{3,4}$/.test(stageTime)) {
    return stageTime;
  }

  const padded = stageTime.padStart(4, "0");
  return `${padded.slice(0, -2)}:${padded.slice(-2)}`;
}

function clearSavedSelection(event: EventConfig) {
  try {
    localStorage.removeItem(getSelectionStorageKey(event));
  } catch {
    // If storage is unavailable, clearing visible state is still enough.
  }
}

function getSelectionStorageKey(event: EventConfig) {
  return `tacplanner:${event.path}:selection`;
}

function getEventDays(event: EventConfig) {
  return event.days ?? days;
}

function eventCardMeta(event: EventConfig) {
  const eventDayCount = getEventDays(event).length;
  const dayLabel = eventDayCount === 1 ? "1 day" : `${eventDayCount} days`;

  return `${Object.keys(event.schedule).length} squads | ${dayLabel}`;
}

function isDay(value: unknown): value is Day {
  return value === "Saturday" || value === "Sunday";
}

function readSavedSelection(event: EventConfig): Selection | null {
  const storageKey = getSelectionStorageKey(event);
  let savedValue: string | null = null;

  try {
    savedValue = localStorage.getItem(storageKey);
  } catch {
    return null;
  }

  if (!savedValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(savedValue) as Partial<Selection>;

    if (
      typeof parsed.matrixNumber === "string" &&
      parsed.matrixNumber in event.schedule &&
      isDay(parsed.day) &&
      getEventDays(event).includes(parsed.day)
    ) {
      return {
        matrixNumber: parsed.matrixNumber,
        day: parsed.day
      };
    }
  } catch {
    // Ignore malformed saved state and remove it below.
  }

  try {
    localStorage.removeItem(storageKey);
  } catch {
    // Storage may be unavailable even if reading worked earlier.
  }

  return null;
}

function saveSelection(event: EventConfig, selection: Selection) {
  try {
    localStorage.setItem(
      getSelectionStorageKey(event),
      JSON.stringify(selection)
    );
  } catch {
    // The selection remains usable for the current render even without storage.
  }
}

async function downloadStagePlanPdf(
  event: EventConfig,
  selection: Selection,
  entries: ScheduleEntry[]
) {
  const { default: jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ format: "letter", unit: "pt" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const pdfTheme = {
    background: "#10100f",
    surface: "#191918",
    inset: "#111110",
    border: "#3a3430",
    accentBorder: "#8f4b25",
    accent: "#ef6723",
    accentLight: "#ff7c3b",
    primary: "#ffffff",
    body: "#d9d3cc",
    muted: "#b7b0a8",
    black: "#100f0d"
  };
  const margin = 36;
  const pageTop = 78;
  const pageBottom = pageHeight - 42;
  const footerY = pageHeight - 24;
  const contentWidth = pageWidth - margin * 2;
  let y = pageTop;

  type PdfLine = {
    color?: string;
    fontSize?: number;
    fontStyle?: "normal" | "bold";
    gapAfter?: number;
    indent?: number;
    text: string;
  };

  type WrappedLine = Required<Omit<PdfLine, "text">> & {
    text: string;
  };

  function colorToRgb(hex: string) {
    const normalized = hex.replace("#", "");
    return [
      Number.parseInt(normalized.slice(0, 2), 16),
      Number.parseInt(normalized.slice(2, 4), 16),
      Number.parseInt(normalized.slice(4, 6), 16)
    ] as const;
  }

  function setFillColor(hex: string) {
    pdf.setFillColor(...colorToRgb(hex));
  }

  function setDrawColor(hex: string) {
    pdf.setDrawColor(...colorToRgb(hex));
  }

  function setTextColor(hex: string) {
    pdf.setTextColor(...colorToRgb(hex));
  }

  function drawPageShell() {
    setFillColor(pdfTheme.background);
    pdf.rect(0, 0, pageWidth, pageHeight, "F");
    setFillColor(pdfTheme.accent);
    pdf.rect(0, 0, pageWidth, 5, "F");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    setTextColor(pdfTheme.accent);
    pdf.text("TACPLANNER", margin, 30);

    pdf.setFontSize(12);
    setTextColor(pdfTheme.primary);
    pdf.text(event.label.toUpperCase(), margin, 48);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    setTextColor(pdfTheme.muted);
    pdf.text(
      `SQUAD ${selection.matrixNumber} | ${selection.day.toUpperCase()}`,
      pageWidth - margin,
      48,
      { align: "right" }
    );
  }

  function addPage() {
    pdf.addPage();
    drawPageShell();
    y = pageTop;
  }

  function ensureSpace(height: number) {
    if (y + height <= pageBottom) {
      return;
    }

    addPage();
  }

  function lineHeight(fontSize: number) {
    return fontSize * 1.28;
  }

  function splitText(text: string, width: number, fontSize: number) {
    pdf.setFontSize(fontSize);
    return pdf.splitTextToSize(text, width) as string[];
  }

  function wrapLines(lines: PdfLine[], width: number): WrappedLine[] {
    return lines.flatMap((line) => {
      const fontSize = line.fontSize ?? 9;
      const indent = line.indent ?? 0;
      const wrapped = splitText(line.text, width - indent, fontSize);

      return wrapped.map((text, index) => ({
        color: line.color ?? pdfTheme.body,
        fontSize,
        fontStyle: line.fontStyle ?? "normal",
        gapAfter: index === wrapped.length - 1 ? (line.gapAfter ?? 5) : 1,
        indent,
        text
      }));
    });
  }

  function labelLine(label: string, value: string | number | null): PdfLine[] {
    if (value === null || value === "") {
      return [];
    }

    return [
      {
        color: pdfTheme.body,
        fontSize: 9,
        gapAfter: 4,
        text: `${label.toUpperCase()}: ${value}`
      }
    ];
  }

  function listLines(values: string[]): PdfLine[] {
    return values.map((value) => ({
      color: pdfTheme.body,
      fontSize: 9,
      gapAfter: 4,
      indent: 8,
      text: `- ${value}`
    }));
  }

  function loadoutLines(loadout: Loadout | null): PdfLine[] {
    if (!loadout) {
      return [];
    }

    return [
      ...labelLine("Rifle", formatLoadoutValue(loadout.rifle)),
      ...labelLine("Pistol", formatLoadoutValue(loadout.pistol)),
      ...labelLine("Notes", loadout.notes)
    ];
  }

  function drawRoundedPanel(x: number, top: number, width: number, height: number) {
    setFillColor(pdfTheme.inset);
    setDrawColor(pdfTheme.border);
    pdf.roundedRect(x, top, width, height, 8, 8, "FD");
  }

  function drawPanel(title: string, lines: PdfLine[]) {
    if (lines.length === 0) {
      return;
    }

    const padding = 11;
    const panelWidth = contentWidth;
    const innerWidth = panelWidth - padding * 2;
    const titleHeight = 14;
    let wrappedLines = wrapLines(lines, innerWidth);
    let titleText = title;

    while (wrappedLines.length > 0) {
      if (pageBottom - y < 90) {
        addPage();
      }

      const availableHeight = pageBottom - y;
      const maxLineHeight = availableHeight - padding * 2 - titleHeight - 7;
      let nextChunkHeight = 0;
      let nextIndex = 0;

      while (nextIndex < wrappedLines.length) {
        const line = wrappedLines[nextIndex];
        const height = lineHeight(line.fontSize) + line.gapAfter;
        if (nextIndex > 0 && nextChunkHeight + height > maxLineHeight) {
          break;
        }

        nextChunkHeight += height;
        nextIndex += 1;
      }

      const chunk = wrappedLines.slice(0, Math.max(1, nextIndex));
      const panelHeight = padding * 2 + titleHeight + 7 + nextChunkHeight;
      drawRoundedPanel(margin, y, panelWidth, panelHeight);

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      setTextColor(pdfTheme.accent);
      pdf.text(titleText.toUpperCase(), margin + padding, y + padding + 8);

      let textY = y + padding + titleHeight + 10;
      chunk.forEach((line) => {
        pdf.setFont("helvetica", line.fontStyle);
        pdf.setFontSize(line.fontSize);
        setTextColor(line.color);
        pdf.text(line.text, margin + padding + line.indent, textY);
        textY += lineHeight(line.fontSize) + line.gapAfter;
      });

      y += panelHeight + 10;
      wrappedLines = wrappedLines.slice(chunk.length);
      titleText = `${title} cont.`;
    }
  }

  function drawTimeBadge(x: number, top: number, entry: ScheduleEntry) {
    setFillColor(pdfTheme.accent);
    pdf.roundedRect(x, top, 72, entry.stageBriefTime ? 58 : 38, 8, 8, "F");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    setTextColor(pdfTheme.black);

    if (entry.stageBriefTime) {
      pdf.text("BRIEF", x + 36, top + 15, { align: "center" });
      pdf.setFontSize(11);
      pdf.text(formatStageTime(entry.stageBriefTime), x + 36, top + 29, {
        align: "center"
      });
      pdf.setFontSize(8);
      pdf.text("STAGE", x + 36, top + 42, { align: "center" });
      pdf.setFontSize(11);
      pdf.text(formatStageTime(entry.stageTime), x + 36, top + 55, {
        align: "center"
      });
      return;
    }

    pdf.setFontSize(12);
    pdf.text(formatStageTime(entry.stageTime), x + 36, top + 24, {
      align: "center"
    });
  }

  function drawSummaryCard(entry: ScheduleEntry, title: string) {
    ensureSpace(88);
    setFillColor(pdfTheme.surface);
    setDrawColor(pdfTheme.border);
    pdf.roundedRect(margin, y, contentWidth, 76, 8, 8, "FD");

    drawTimeBadge(margin + 12, y + 10, entry);

    const textX = margin + 98;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    setTextColor(pdfTheme.accent);
    pdf.text(entry.stage.toUpperCase(), textX, y + 24);

    const titleLines = splitText(title, contentWidth - 118, 15).slice(0, 2);
    pdf.setFontSize(15);
    setTextColor(pdfTheme.primary);
    pdf.text(titleLines, textX, y + 44);

    y += 88;
  }

  function drawSafetyCard(entry: ScheduleEntry) {
    ensureSpace(76);
    setFillColor(pdfTheme.surface);
    setDrawColor(pdfTheme.border);
    pdf.roundedRect(margin, y, contentWidth, 64, 8, 8, "FD");
    drawTimeBadge(margin + 12, y + 13, entry);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    setTextColor(pdfTheme.primary);
    pdf.text("Safety Brief", margin + 98, y + 28);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    setTextColor(pdfTheme.muted);
    pdf.text("Mandatory for all competitors", margin + 98, y + 45);

    y += 76;
  }

  function drawStagePanels(stage: StageInfo) {
    drawPanel("Stage Details", [
      ...labelLine("Bay", stage.bayLocation),
      ...labelLine("Type", stage.type),
      ...labelLine("Cap", stage.cap),
      ...labelLine("RO", stage.ro),
      ...labelLine("Gear", stage.gear),
      ...labelLine("Firing Sequence", stage.firingSequence),
      ...labelLine("Targets", stage.targets)
    ]);

    drawPanel("Loadout", loadoutLines(stage.loadout));

    if (stage.stageDescription) {
      drawPanel("Stage Description", [
        {
          color: pdfTheme.body,
          fontSize: 9,
          text: stage.stageDescription
        }
      ]);
    }

    drawPanel("Movement Standards", listLines(stage.movementStandards));
    drawPanel("Penalties", listLines(stage.penalties));
    drawPanel("Reps", listLines(stage.reps));
    drawPanel("Weights", listLines(stage.weights));
    drawPanel("Calories and Weights", listLines(stage.caloriesAndWeights));

    if (stage.teams) {
      drawPanel("Teams", [
        ...loadoutLines(stage.teams.loadout),
        ...labelLine("Firing Sequence", stage.teams.firingSequence),
        ...(stage.teams.stageDescription
          ? [
              {
                color: pdfTheme.body,
                fontSize: 9,
                gapAfter: 0,
                text: stage.teams.stageDescription
              }
            ]
          : [])
      ]);
    }
  }

  pdf.setProperties({
    title: `${event.label} Squad ${selection.matrixNumber} ${selection.day}`,
    subject: "TacPlanner stage plan"
  });

  drawPageShell();

  entries.forEach((entry, index) => {
    if (index > 0) {
      y += 2;
    }

    if (entry.stage === "Safety Brief") {
      drawSafetyCard(entry);
      return;
    }

    const stageNumber = readStageNumber(entry.stage);
    const stage = stageNumber ? event.stages[String(stageNumber)] : undefined;
    drawSummaryCard(entry, stage?.title ?? "Details unavailable");

    if (!stage) {
      drawPanel("Stage Details", [
        {
          color: pdfTheme.body,
          fontSize: 9,
          text: "Stage details are not available."
        }
      ]);
      return;
    }

    drawStagePanels(stage);
  });

  const pageCount = pdf.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    pdf.setPage(page);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    setTextColor(pdfTheme.muted);
    pdf.text(`Page ${page} of ${pageCount}`, pageWidth - margin, footerY, {
      align: "right"
    });
  }

  pdf.save(getStagePlanFilename(event, selection));
}

function getStagePlanFilename(event: EventConfig, selection: Selection) {
  const eventSlug = event.path.replace(/^\//, "") || "event";
  return `tacplanner-${eventSlug}-squad-${selection.matrixNumber}-${selection.day.toLowerCase()}.pdf`;
}

function normalizePath(pathname: string) {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }

  return pathname;
}
